import Panzoom from "@panzoom/panzoom";
import "../css/trendradar-viewer.css";

/** Duration in ms the scroll hint remains visible after the last plain scroll. */
const HINT_TIMEOUT_MS = 1500;

/**
 * Alpine.js component for the interactive Trend Radar viewer.
 *
 * Wraps @panzoom/panzoom to provide pan and zoom on the radar image.
 * Registered in app.js as Alpine.data("trendRadarViewer", trendRadarViewer).
 *
 * Optional `initial.detailByPoint`: map of point id → `{ label: string, detail: string }`
 * for points with non-empty detail text (built in trendradar.twig). Clicking the dot or
 * label opens a modal showing `detail`; the modal title uses `label` (short label).
 * The modal overlay is `position: absolute` within the viewer root (not the full viewport).
 *
 * Output parity:
 * - The viewer intentionally reproduces the CP editor output by using the same
 *   stored percent coordinates (`x/y`, `labelX/labelY`) and sizing metadata in
 *   natural image pixels (`pointDiameterPx`, `labelWidthPx`, etc.).
 * - A CSS variable `--trendradar-scale` is applied to the panzoom target based on:
 *     scale = displayedImageWidth / naturalImageWidth
 *   so CSS can scale point/label sizes deterministically across screen sizes.
 *
 * Connector geometry:
 * - Connector paths are computed in percent space (SVG viewBox 0..100) rather than
 *   from DOM measurements to avoid resolution/zoom-dependent drift.
 *
 * Zoom behaviour:
 *   - Ctrl + scroll (or pinch on trackpad) → zooms the radar
 *   - Plain scroll → passes through to the page; briefly shows a hint overlay
 *
 * Expected markup (see trendradar/components/trendradar.twig):
 *   - x-data="trendRadarViewer(...)" on the root (pass `{ detailByPoint }` from Twig or omit)
 *   - optional x-ref="detailJson" on a `<script type="application/json">` with the same map
 *   - x-ref="panzoomContainer" on the overflow wrapper (receives the wheel listener)
 *   - x-ref="panzoomTarget" on the element to pan/zoom (wraps the image and points)
 *   - x-show="scrollHint" on the hint overlay element
 *
 * @param {{ detailByPoint?: Record<string, { label: string, detail: string }> }} [initial]
 * @returns {object} Alpine component data object
 */
export default function trendRadarViewer(initial = {}) {
	const fromInitial =
		initial &&
		typeof initial.detailByPoint === "object" &&
		initial.detailByPoint !== null &&
		!Array.isArray(initial.detailByPoint)
			? initial.detailByPoint
			: null;

	return {
		/** @type {import("@panzoom/panzoom").PanzoomObject|null} */
		panzoom: null,

		/** Map of point id → { label, detail } for modal content (plain text). */
		detailByPoint: {},

		/** Modal open state for optional point detail text. */
		detailModalOpen: false,

		/** Modal title (short label). */
		detailModalTitle: "",

		/** Modal body (multiline plain text). */
		detailModalBody: "",

		/** Element to restore focus to when the modal closes. */
		_detailModalTriggerEl: null,

		/** @type {(e: KeyboardEvent) => void} */
		_escapeHandler: null,

		/** Controls visibility of the "use Ctrl + scroll" hint overlay. */
		scrollHint: false,

		/** @type {ReturnType<typeof setTimeout>|null} */
		_hintTimer: null,
		_resizeHandler: null,
		_hoverHandler: null,
		_activeHoverPointId: null,

		/**
		 * Initialises Panzoom on the target element and binds the wheel listener.
		 * Ctrl + scroll zooms; plain scroll lets the page scroll and shows a hint.
		 * Called automatically by Alpine on mount.
		 */
		init() {
			const jsonEl = this.$refs.detailJson;
			if (jsonEl?.textContent?.trim()) {
				try {
					const parsed = JSON.parse(jsonEl.textContent.trim());
					if (
						parsed &&
						typeof parsed === "object" &&
						!Array.isArray(parsed)
					) {
						this.detailByPoint = parsed;
					}
				} catch {
					this.detailByPoint = {};
				}
			} else if (fromInitial && Object.keys(fromInitial).length > 0) {
				this.detailByPoint = { ...fromInitial };
			}

			this.panzoom = Panzoom(this.$refs.panzoomTarget, {
				maxScale: 5,
				minScale: 0.2,
				startScale: 1,
				contain: "none",
				cursor: "default",
			});
			const image = this.$refs.panzoomTarget.querySelector("img");
			const initLayout = () => {
				requestAnimationFrame(() => {
					this.zoomToFit();
					this.updateScaleVar();
					this.layoutConnectors();
				});
			};
			if (image.complete && image.naturalWidth > 0) {
				initLayout();
			} else {
				image.addEventListener("load", initLayout, { once: true });
			}

			this._resizeHandler = () => {
				this.updateScaleVar();
				this.layoutConnectors();
			};
			window.addEventListener("resize", this._resizeHandler);

			this._hoverHandler = (e) => this.handleHoverEvent(e);
			this.$refs.panzoomTarget.addEventListener(
				"pointerover",
				this._hoverHandler,
			);
			this.$refs.panzoomTarget.addEventListener(
				"pointerout",
				this._hoverHandler,
			);

			this.$refs.panzoomContainer.addEventListener(
				"wheel",
				(e) => {
					if (e.ctrlKey) {
						e.preventDefault();
						this._hideScrollHint();
						this.panzoom.zoomWithWheel(e);
						this.updateScaleVar();
						this.layoutConnectors();
					} else {
						this._showScrollHint();
					}
				},
				{ passive: false },
			);

			this._escapeHandler = (e) => {
				if (e.key === "Escape" && this.detailModalOpen) {
					e.preventDefault();
					this.closeDetailModal();
				}
			};
			window.addEventListener("keydown", this._escapeHandler);
		},

		/**
		 * Opens the detail modal when the point has non-empty detail text.
		 *
		 * @param {string} pointId
		 * @param {Event} [event]
		 * @returns {void}
		 */
		openDetailModal(pointId, event) {
			const entry = this.detailByPoint[pointId];
			if (!entry?.detail?.trim()) return;
			if (event?.stopPropagation) event.stopPropagation();
			this._detailModalTriggerEl =
				event?.currentTarget instanceof HTMLElement
					? event.currentTarget
					: null;
			const fallback =
				this.$el?.dataset?.detailTitleFallback?.trim() ?? "";
			this.detailModalTitle = entry.label?.trim() || fallback;
			this.detailModalBody = entry.detail.trim();
			this.detailModalOpen = true;
			requestAnimationFrame(() => {
				this.$refs.detailModalClose?.focus();
			});
		},

		/**
		 * Closes the detail modal and restores focus to the original trigger element.
		 *
		 * @returns {void}
		 */
		closeDetailModal() {
			this.detailModalOpen = false;
			this.detailModalTitle = "";
			this.detailModalBody = "";
			const t = this._detailModalTriggerEl;
			this._detailModalTriggerEl = null;
			if (t?.focus) t.focus();
		},

		computeFitTransform() {
			const container = this.$refs.panzoomContainer;
			const image = this.$refs.panzoomTarget.querySelector("img");
			const canvasW = container.clientWidth;
			const canvasH = container.clientHeight;
			const aspect = image.naturalHeight / image.naturalWidth;
			const cssImageH = aspect * canvasW;
			const fitScale = Math.min(1, canvasH / cssImageH);
			return {
				fitScale,
				startX: 0,
				startY: (canvasH - cssImageH) / (2 * fitScale),
			};
		},

		zoomToFit(animate = false) {
			const { fitScale, startX, startY } = this.computeFitTransform();
			this.panzoom.setOptions({ startScale: fitScale, startX, startY });
			this.panzoom.reset({ animate });
		},

		layoutConnectors() {
			const target = this.$refs.panzoomTarget;
			const svg = target.querySelector(".trendradar-point-connectors");
			if (!svg) return;

			const image = target.querySelector("img");
			if (!image?.naturalWidth) return;

			const dotMap = new Map(
				Array.from(target.querySelectorAll(".trendradar-point")).map(
					(dot) => [dot.dataset.pointId, dot],
				),
			);
			const labelEls = Array.from(
				target.querySelectorAll(".trendradar-point__label"),
			);

			const paths = [];
			for (const label of labelEls) {
				const id = label.dataset.pointId;
				if (!id) continue;
				const dot = dotMap.get(id);
				if (!dot) continue;

				const x = Number.parseFloat(
					label.dataset.pointX ?? dot.dataset.pointX ?? "",
				);
				const y = Number.parseFloat(
					label.dataset.pointY ?? dot.dataset.pointY ?? "",
				);
				const labelX = Number.parseFloat(label.dataset.labelX ?? "");
				const labelY = Number.parseFloat(label.dataset.labelY ?? "");
				if (![x, y, labelX, labelY].every(Number.isFinite)) continue;

				const labelWidthPercent = this._resolveLabelWidthPercent(
					label,
					image,
				);
				const labelCenterX = labelX + labelWidthPercent / 2;
				const labelAnchorX =
					x <= labelCenterX ? labelX : labelX + labelWidthPercent;
				const direction = labelAnchorX >= x ? 1 : -1;
				const pointRadiusPercent = this._resolvePointRadiusPercent(
					dot,
					image,
				);
				const pointEdgeX = x + direction * pointRadiusPercent;
				const midX = (pointEdgeX + labelAnchorX) / 2;

				paths.push(
					`<path class="trendradar-point-connector" data-point-id="${id}" d="M ${this._round2(pointEdgeX)} ${this._round2(y)} H ${this._round2(midX)} V ${this._round2(labelY)} H ${this._round2(labelAnchorX)}"></path>`,
				);
			}

			svg.innerHTML = paths.join("");
		},

		/**
		 * Handles hover interactions for dot/label/connector.
		 * Highlights all three elements for the same point id.
		 *
		 * @param {PointerEvent} e
		 */
		handleHoverEvent(e) {
			const target = e.target;
			const pointId =
				target?.closest?.("[data-point-id]")?.dataset?.pointId ?? null;

			if (e.type === "pointerover") {
				if (!pointId) return;
				this.setActiveHoverPoint(pointId);
				return;
			}

			// pointerout: only clear when leaving the entire point/label/connector group
			const related = e.relatedTarget;
			const relatedPointId =
				related?.closest?.("[data-point-id]")?.dataset?.pointId ?? null;
			if (relatedPointId === pointId) return;

			if (!relatedPointId) {
				this.setActiveHoverPoint(null);
			} else {
				this.setActiveHoverPoint(relatedPointId);
			}
		},

		/**
		 * Applies/removes highlight classes for the current hover id.
		 *
		 * @param {string|null} pointId
		 */
		setActiveHoverPoint(pointId) {
			if (this._activeHoverPointId === pointId) return;
			this._activeHoverPointId = pointId;

			const root = this.$refs.panzoomTarget;
			const isActive = (el) =>
				pointId &&
				el?.dataset?.pointId &&
				el.dataset.pointId === pointId;

			root.querySelectorAll(
				".trendradar-point, .trendradar-point__label, .trendradar-point-connector",
			).forEach((el) => {
				el.classList.toggle("trendradar-hover--active", isActive(el));
			});
		},

		/**
		 * Updates `--trendradar-scale` on the panzoom target so CSS can scale
		 * natural-px based sizes to CSS pixels deterministically.
		 */
		updateScaleVar() {
			const target = this.$refs.panzoomTarget;
			const image = target?.querySelector("img");
			if (!image?.naturalWidth) return;
			const scale = image.clientWidth / image.naturalWidth;
			if (!Number.isFinite(scale) || scale <= 0) return;
			target.style.setProperty("--trendradar-scale", String(scale));
		},

		/**
		 * @param {HTMLElement} labelEl
		 * @param {HTMLImageElement} image
		 * @returns {number} Label width in percent (0..100)
		 */
		_resolveLabelWidthPercent(labelEl, image) {
			const widthPx = Number.parseFloat(
				labelEl.dataset.labelWidthPx ?? "",
			);
			if (Number.isFinite(widthPx) && widthPx > 0) {
				return (widthPx / image.naturalWidth) * 100;
			}

			return 0;
		},

		/**
		 * @param {HTMLElement} dotEl
		 * @param {HTMLImageElement} image
		 * @returns {number} Point radius in percent (0..100)
		 */
		_resolvePointRadiusPercent(dotEl, image) {
			const diameterPx = Number.parseFloat(
				this.$refs?.panzoomTarget?.dataset?.pointDiameterPx ?? "",
			);
			const safeDiameterPx =
				Number.isFinite(diameterPx) && diameterPx > 0 ? diameterPx : 14;
			return (safeDiameterPx / 2 / image.naturalWidth) * 100;
		},

		/**
		 * @param {number} value
		 * @returns {number}
		 */
		_round2(value) {
			return Math.round(value * 100) / 100;
		},

		/**
		 * Shows the scroll hint overlay and hides it again after HINT_TIMEOUT_MS.
		 * Resets the timer on each call so rapid scrolling keeps the hint visible.
		 */
		_showScrollHint() {
			this.scrollHint = true;
			clearTimeout(this._hintTimer);
			this._hintTimer = setTimeout(() => {
				this.scrollHint = false;
			}, HINT_TIMEOUT_MS);
		},

		/** Immediately cancels the hint timer and hides the overlay. */
		_hideScrollHint() {
			clearTimeout(this._hintTimer);
			this._hintTimer = null;
			this.scrollHint = false;
		},

		/** Zooms in one step using the Panzoom default increment. */
		zoomIn() {
			this.panzoom?.zoomIn();
			this.updateScaleVar();
			this.layoutConnectors();
		},

		/** Zooms out one step using the Panzoom default increment. */
		zoomOut() {
			this.panzoom?.zoomOut();
			this.updateScaleVar();
			this.layoutConnectors();
		},

		/** Resets pan position and zoom level to their initial values. */
		resetZoom() {
			this.zoomToFit(true);
			this.updateScaleVar();
			this.layoutConnectors();
		},

		/** Destroys the Panzoom instance and clears any pending hint timer. */
		destroy() {
			this.closeDetailModal();
			this._hideScrollHint();
			if (this._escapeHandler) {
				window.removeEventListener("keydown", this._escapeHandler);
				this._escapeHandler = null;
			}
			if (this._resizeHandler) {
				window.removeEventListener("resize", this._resizeHandler);
				this._resizeHandler = null;
			}
			if (this._hoverHandler) {
				this.$refs.panzoomTarget?.removeEventListener(
					"pointerover",
					this._hoverHandler,
				);
				this.$refs.panzoomTarget?.removeEventListener(
					"pointerout",
					this._hoverHandler,
				);
				this._hoverHandler = null;
			}
			this.panzoom?.destroy();
		},
	};
}
